import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, AlertTriangle, Activity, Users, Wallet, CalendarDays,
  TrendingUp, TrendingDown, Minus, LayoutGrid,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, LabelList,
} from "recharts";
import { getAlocacaoPessoal, getColaboradoresMaoDeObra, getProjetos } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";

const MESES_1_12 = Array.from({ length: 12 }, (_, idx) => idx + 1);
const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const MAX_COLABORADORES_OCIOSIDADE = 15;

// Paleta categórica fixa (ordem sempre igual, nunca ciclada por filtro) — só para o comparativo
// de headcount por projeto, onde cor identifica o projeto, não um status.
const CORES_PROJETOS = ["#7C3AED", "#0EA5E9", "#F59E0B", "#10B981", "#EC4899", "#6366F1", "#14B8A6", "#F97316"];
function corDoProjeto(indice) {
  return CORES_PROJETOS[indice % CORES_PROJETOS.length];
}

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function somaMes(colaborador, mes) {
  return (colaborador.alocacoes_por_mes[mes] || []).reduce((acc, a) => acc + a.pct, 0);
}

function fmtCompact(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1)}K`;
  return formatCurrency(n);
}

function truncar(texto, max = 26) {
  if (!texto || texto.length <= max) return texto;
  return `${texto.slice(0, max - 1)}…`;
}

// Mesmo critério de cor da grade de Alocação de Pessoal: <50% vermelho, 50–99% amarelo, 100% verde.
function corPct(C, pct) {
  if (pct === 100) return C.success;
  if (pct >= 50) return C.warning;
  if (pct > 0) return C.danger;
  return C.muted;
}

function KpiCard({ C, isLight, icon: Icon, cor, label, valor, subtitulo }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 6,
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7, background: `${cor}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={13} color={cor} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, color: C.text }}>{valor}</div>
      {subtitulo && <div style={{ fontSize: 10.5, color: C.muted }}>{subtitulo}</div>}
    </div>
  );
}

function ChartCard({ C, isLight, titulo, subtitulo, height, children }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20,
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
    }}>
      <span style={{ fontWeight: 700, fontSize: 13, color: C.text, display: "block", marginBottom: 2 }}>
        {titulo}
      </span>
      <span style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 16 }}>
        {subtitulo}
      </span>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function VisaoExecutivaView({ tituloModulo }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [colaboradores, setColaboradores] = useState([]);
  const [custosPorColaborador, setCustosPorColaborador] = useState({});
  const [projetos, setProjetos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [projetoSelecionadoId, setProjetoSelecionadoId] = useState(null);
  const [mesSelecionado, setMesSelecionado] = useState(() => new Date().getMonth() + 1);

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      setLoading(true);
      setErro("");
      try {
        const [alocacao, maoDeObra, listaProjetos] = await Promise.all([
          getAlocacaoPessoal(ano),
          getColaboradoresMaoDeObra(ano),
          getProjetos(),
        ]);
        if (cancelado) return;
        setColaboradores(alocacao || []);
        setCustosPorColaborador(Object.fromEntries((maoDeObra || []).map((c) => [c.id, c.meses_custo_total || {}])));
        setProjetos(listaProjetos || []);
      } catch (e) {
        if (!cancelado) setErro(e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    carregar();
    return () => { cancelado = true; };
  }, [ano]);

  const projetoNomePorId = useMemo(() => Object.fromEntries(projetos.map((p) => [p.id, p.nome])), [projetos]);

  // Gráfico 1 — saúde da alocação ao longo do tempo: média mensal do time entre
  // % alocado (capado a 100), % ocioso e % sobrealocado (o quanto passou de 100).
  const saudeAlocacao = useMemo(() => {
    const n = colaboradores.length;
    return MESES_1_12.map((mes) => {
      if (n === 0) return { mes: MESES_ABREV[mes - 1], alocado: 0, ocioso: 0, sobrealocado: 0 };
      let alocado = 0, ocioso = 0, sobrealocado = 0;
      colaboradores.forEach((c) => {
        const total = somaMes(c, mes);
        alocado += Math.min(total, 100);
        ocioso += Math.max(0, 100 - total);
        sobrealocado += Math.max(0, total - 100);
      });
      return {
        mes: MESES_ABREV[mes - 1],
        alocado: Number((alocado / n).toFixed(1)),
        ocioso: Number((ocioso / n).toFixed(1)),
        sobrealocado: Number((sobrealocado / n).toFixed(1)),
      };
    });
  }, [colaboradores]);

  // Gráfico 3 — ranking de projetos por custo de pessoal acumulado no ano: para cada
  // colaborador × mês, soma (% alocado no projeto) × (custo total do colaborador no mês).
  const custoPorProjeto = useMemo(() => {
    const totais = {};
    colaboradores.forEach((c) => {
      const custosMensais = custosPorColaborador[c.id] || {};
      MESES_1_12.forEach((mes) => {
        const custoDoMes = Number(custosMensais[mes] || 0);
        if (!custoDoMes) return;
        (c.alocacoes_por_mes[mes] || []).forEach((a) => {
          totais[a.projeto_id] = (totais[a.projeto_id] || 0) + (a.pct / 100) * custoDoMes;
        });
      });
    });
    return Object.entries(totais)
      .map(([projetoId, total]) => ({
        projetoCompleto: projetoNomePorId[projetoId] || `Projeto ${projetoId}`,
        projeto: truncar(projetoNomePorId[projetoId] || `Projeto ${projetoId}`),
        total,
      }))
      .filter((p) => p.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [colaboradores, custosPorColaborador, projetoNomePorId]);

  // Gráfico 5 — ranking de ociosidade acumulada: soma, colaborador a colaborador, de
  // (100% − alocação do mês) em todos os meses do ano; expresso também em "meses ociosos".
  const ociosidadeAcumulada = useMemo(() => {
    return colaboradores
      .map((c) => {
        const pctAcumulado = MESES_1_12.reduce((acc, mes) => acc + Math.max(0, 100 - somaMes(c, mes)), 0);
        const nomeCompleto = c.ocupante ? `${c.cargo} · ${c.ocupante}` : `${c.cargo} · vaga em aberto`;
        return {
          colaboradorCompleto: nomeCompleto,
          colaborador: truncar(nomeCompleto),
          mesesOciosos: Number((pctAcumulado / 100).toFixed(1)),
          pctAcumulado,
        };
      })
      .filter((c) => c.pctAcumulado > 0)
      .sort((a, b) => b.pctAcumulado - a.pctAcumulado)
      .slice(0, MAX_COLABORADORES_OCIOSIDADE);
  }, [colaboradores]);

  // Visão por Projeto — agrega, por projeto e por mês: headcount (nº de colaboradores com
  // pct > 0), custo de pessoal (Σ % alocado × custo do colaborador no mês) e a lista de
  // colaboradores alocados naquele mês (nome + % + custo individual), base de tudo que os
  // KPIs/gráficos/comparativo desta seção precisam.
  const resumoPorProjeto = useMemo(() => {
    const mapa = {};
    colaboradores.forEach((c) => {
      const custosMensais = custosPorColaborador[c.id] || {};
      const nomeCompleto = c.ocupante ? `${c.cargo} · ${c.ocupante}` : `${c.cargo} · vaga em aberto`;
      MESES_1_12.forEach((mes) => {
        const custoColabMes = Number(custosMensais[mes] || 0);
        (c.alocacoes_por_mes[mes] || []).forEach((a) => {
          if (!mapa[a.projeto_id]) {
            mapa[a.projeto_id] = {
              projetoId: a.projeto_id,
              headcountPorMes: Object.fromEntries(MESES_1_12.map((m) => [m, 0])),
              custoPorMes: Object.fromEntries(MESES_1_12.map((m) => [m, 0])),
              colaboradoresPorMes: Object.fromEntries(MESES_1_12.map((m) => [m, []])),
            };
          }
          const entrada = mapa[a.projeto_id];
          const custoAlocado = (a.pct / 100) * custoColabMes;
          entrada.headcountPorMes[mes] += 1;
          entrada.custoPorMes[mes] += custoAlocado;
          entrada.colaboradoresPorMes[mes].push({ nome: nomeCompleto, pct: a.pct, custo: custoAlocado });
        });
      });
    });
    return mapa;
  }, [colaboradores, custosPorColaborador]);

  // Comparativo entre projetos — headcount e custo lado a lado, ordenado por custo acumulado
  // no ano (maior peso na folha primeiro); clicar numa linha seleciona o projeto abaixo.
  const comparativoProjetos = useMemo(() => {
    return Object.values(resumoPorProjeto)
      .map((r) => ({
        projetoId: r.projetoId,
        projetoNome: projetoNomePorId[r.projetoId] || `Projeto ${r.projetoId}`,
        headcountMes: r.headcountPorMes[mesSelecionado] || 0,
        custoMes: r.custoPorMes[mesSelecionado] || 0,
        custoAno: MESES_1_12.reduce((acc, m) => acc + r.custoPorMes[m], 0),
      }))
      .sort((a, b) => b.custoAno - a.custoAno);
  }, [resumoPorProjeto, projetoNomePorId, mesSelecionado]);

  // Auto-seleciona o projeto de maior peso na folha assim que os dados chegam, sem sobrescrever
  // uma escolha que o usuário já tenha feito.
  useEffect(() => {
    if (projetoSelecionadoId === null && comparativoProjetos.length > 0) {
      setProjetoSelecionadoId(comparativoProjetos[0].projetoId);
    }
  }, [comparativoProjetos, projetoSelecionadoId]);

  // Evolução do headcount, comparando TODOS os projetos ao mesmo tempo (1 linha por projeto,
  // mesma ordem/cor do comparativo acima) — diferente dos demais gráficos desta seção, não
  // depende do projeto selecionado, é sempre visão de portfólio.
  const projetosParaComparar = useMemo(() => comparativoProjetos.map((p) => p.projetoNome), [comparativoProjetos]);
  const headcountPorProjetoEvolucao = useMemo(() => {
    return MESES_1_12.map((mes) => {
      const ponto = { mes: MESES_ABREV[mes - 1] };
      comparativoProjetos.forEach((p) => {
        const r = resumoPorProjeto[p.projetoId];
        ponto[p.projetoNome] = r ? r.headcountPorMes[mes] : 0;
      });
      return ponto;
    });
  }, [comparativoProjetos, resumoPorProjeto]);

  const detalheProjeto = useMemo(() => {
    const r = projetoSelecionadoId != null ? resumoPorProjeto[projetoSelecionadoId] : null;
    if (!r) return null;

    const colaboradoresMes = r.colaboradoresPorMes[mesSelecionado] || [];
    const headcountMes = colaboradoresMes.length;
    const pctMedio = headcountMes ? colaboradoresMes.reduce((acc, c) => acc + c.pct, 0) / headcountMes : 0;
    const custoMes = r.custoPorMes[mesSelecionado] || 0;
    const custoAno = MESES_1_12.reduce((acc, m) => acc + r.custoPorMes[m], 0);

    const mesesAtivos = MESES_1_12.filter((m) => r.headcountPorMes[m] > 0);
    const primeiroMesAtivo = mesesAtivos[0];
    const ultimoMesAtivo = mesesAtivos[mesesAtivos.length - 1];
    const headcountInicio = primeiroMesAtivo ? r.headcountPorMes[primeiroMesAtivo] : 0;
    const headcountFim = ultimoMesAtivo ? r.headcountPorMes[ultimoMesAtivo] : 0;
    const variacao = headcountFim > headcountInicio ? "crescendo" : headcountFim < headcountInicio ? "caindo" : "estavel";

    return {
      headcountMes, pctMedio, custoMes, custoAno,
      nMesesAtivo: mesesAtivos.length, variacao, headcountInicio, headcountFim,
      primeiroMesAtivo, ultimoMesAtivo,
      custoEvolucao: MESES_1_12.map((m) => ({ mes: MESES_ABREV[m - 1], custo: r.custoPorMes[m] })),
      alocacaoColaboradoresMes: colaboradoresMes
        .map((c) => ({ colaboradorCompleto: c.nome, colaborador: truncar(c.nome), pct: c.pct }))
        .sort((a, b) => b.pct - a.pct),
      rankingCustoColaboradoresMes: colaboradoresMes
        .map((c) => ({ colaboradorCompleto: c.nome, colaborador: truncar(c.nome), custo: c.custo }))
        .filter((c) => c.custo > 0)
        .sort((a, b) => b.custo - a.custo),
    };
  }, [projetoSelecionadoId, resumoPorProjeto, mesSelecionado]);

  const tooltipStyle = { backgroundColor: C.card, borderColor: C.border, borderRadius: 8, color: C.text, fontSize: 12 };
  const semDados = !loading && colaboradores.length === 0;
  const projetoSelecionadoNome = projetoNomePorId[projetoSelecionadoId] || "";
  const VARIACAO_INFO = {
    crescendo: { icon: TrendingUp, cor: C.success, label: "Crescendo" },
    caindo: { icon: TrendingDown, cor: C.danger, label: "Caindo" },
    estavel: { icon: Minus, cor: C.muted, label: "Estável" },
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
              <Activity size={16} color={C.brand} /> {tituloModulo} · OPERAÇÃO · {ano}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Cruza a Alocação de Pessoal com o Custo Mão de Obra individual de cada colaborador da OPERAÇÃO.
            </p>
          </div>

          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : semDados ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              Nenhum colaborador da OPERAÇÃO cadastrado em Custo Mão de Obra para {ano}.
            </div>
          ) : (
            <>
                          <div>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                  <LayoutGrid size={16} color={C.brand} /> Visão por Projeto
                </h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
                  Recorta headcount, % de alocação e custo de pessoal para um único projeto — clique numa linha do comparativo ou use o seletor.
                </p>
              </div>

              {comparativoProjetos.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                  Nenhum colaborador alocado a projetos em {ano}.
                </div>
              ) : (
                <>
                  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 13, color: C.text, display: "block", marginBottom: 2 }}>Comparativo entre projetos</span>
                        <span style={{ fontSize: 11, color: C.muted }}>Headcount e custo lado a lado — ordenado por custo acumulado em {ano}. Clique numa linha para ver o detalhe abaixo.</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Mês</span>
                        <select value={mesSelecionado} onChange={(e) => setMesSelecionado(Number(e.target.value))}
                          style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "6px 10px", fontFamily: "inherit" }}>
                          {MESES_ABREV.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Projeto</th>
                            <th style={{ textAlign: "right", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Headcount ({MESES_ABREV[mesSelecionado - 1]})</th>
                            <th style={{ textAlign: "right", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Custo ({MESES_ABREV[mesSelecionado - 1]})</th>
                            <th style={{ textAlign: "right", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Custo acumulado ({ano})</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparativoProjetos.map((p) => {
                            const selecionado = p.projetoId === projetoSelecionadoId;
                            return (
                              <tr
                                key={p.projetoId} onClick={() => setProjetoSelecionadoId(p.projetoId)}
                                style={{ cursor: "pointer", background: selecionado ? `${C.brand}14` : "transparent" }}
                                onMouseEnter={(e) => { if (!selecionado) e.currentTarget.style.background = C.bg; }}
                                onMouseLeave={(e) => { if (!selecionado) e.currentTarget.style.background = "transparent"; }}
                              >
                                <td style={{ padding: "8px 10px", fontWeight: selecionado ? 700 : 600, color: selecionado ? C.brand : C.text, borderBottom: `1px solid ${C.border}` }}>{p.projetoNome}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", color: C.text, borderBottom: `1px solid ${C.border}` }}>{p.headcountMes}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", color: C.text, borderBottom: `1px solid ${C.border}` }}>{formatCurrency(p.custoMes)}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: C.text, borderBottom: `1px solid ${C.border}` }}>{fmtCompact(p.custoAno)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <ChartCard C={C} isLight={isLight} height={280} titulo="Evolução do headcount alocado — comparativo entre projetos"
                    subtitulo={`Nº de pessoas alocadas em cada projeto, mês a mês em ${ano} — ${projetoSelecionadoNome ? `${projetoSelecionadoNome} em destaque` : "clique num projeto no comparativo para destacar"}`}>
                    <LineChart data={headcountPorProjetoEvolucao} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                      <XAxis dataKey="mes" stroke={C.muted} fontSize={12} tickLine={false} axisLine={false} dy={6} />
                      <YAxis stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} pessoa${v === 1 ? "" : "s"}`, null]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {projetosParaComparar.map((nome, i) => {
                        const destacado = nome === projetoSelecionadoNome;
                        return (
                          <Line
                            key={nome} type="monotone" dataKey={nome} name={nome} stroke={corDoProjeto(i)}
                            strokeWidth={destacado ? 3 : 1.5} strokeOpacity={destacado || !projetoSelecionadoNome ? 1 : 0.45}
                            dot={{ r: destacado ? 3 : 2 }} activeDot={{ r: 6 }}
                          />
                        );
                      })}
                    </LineChart>
                  </ChartCard>

                  {detalheProjeto && (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}>
                        <KpiCard C={C} isLight={isLight} icon={Users} cor={C.brand} label="Pessoas alocadas"
                          valor={detalheProjeto.headcountMes}
                          subtitulo={`em ${MESES_ABREV[mesSelecionado - 1]}/${ano} · ${projetoSelecionadoNome}`} />
                        <KpiCard C={C} isLight={isLight} icon={Activity} cor={corPct(C, Math.round(detalheProjeto.pctMedio))} label="% médio de alocação"
                          valor={`${detalheProjeto.pctMedio.toFixed(1)}%`}
                          subtitulo={`média das ${detalheProjeto.headcountMes} pessoas no mês`} />
                        <KpiCard C={C} isLight={isLight} icon={Wallet} cor={C.brand} label="Custo de pessoal"
                          valor={fmtCompact(detalheProjeto.custoMes)}
                          subtitulo={`no mês · ${fmtCompact(detalheProjeto.custoAno)} acumulado em ${ano}`} />
                        <KpiCard C={C} isLight={isLight} icon={CalendarDays} cor={C.brand} label="Meses ativo"
                          valor={`${detalheProjeto.nMesesAtivo}/12`}
                          subtitulo={`meses com alguma alocação em ${ano}`} />
                        <KpiCard C={C} isLight={isLight} icon={VARIACAO_INFO[detalheProjeto.variacao].icon} cor={VARIACAO_INFO[detalheProjeto.variacao].cor} label="Variação de headcount"
                          valor={VARIACAO_INFO[detalheProjeto.variacao].label}
                          subtitulo={detalheProjeto.primeiroMesAtivo ? `${detalheProjeto.headcountInicio} (${MESES_ABREV[detalheProjeto.primeiroMesAtivo - 1]}) → ${detalheProjeto.headcountFim} (${MESES_ABREV[detalheProjeto.ultimoMesAtivo - 1]})` : "sem alocação no ano"} />
                      </div>

                      <ChartCard C={C} isLight={isLight} height={240} titulo="Evolução do custo de pessoal"
                        subtitulo={`Custo de pessoal de ${projetoSelecionadoNome}, mês a mês`}>
                        <AreaChart data={detalheProjeto.custoEvolucao} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="custoProjetoFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={C.brand} stopOpacity={0.35} />
                              <stop offset="95%" stopColor={C.brand} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                          <XAxis dataKey="mes" stroke={C.muted} fontSize={12} tickLine={false} axisLine={false} dy={6} />
                          <YAxis stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={48} />
                          <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatCurrency(v), "Custo de pessoal"]} />
                          <Area type="monotone" dataKey="custo" name="Custo de pessoal" stroke={C.brand} strokeWidth={2} fill="url(#custoProjetoFill)" />
                        </AreaChart>
                      </ChartCard>

                      {/* <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                        <ChartCard C={C} isLight={isLight} height={Math.max(220, detalheProjeto.alocacaoColaboradoresMes.length * 30)}
                          titulo="Alocação por colaborador"
                          subtitulo={`% de alocação de cada colaborador em ${projetoSelecionadoNome}, em ${MESES_ABREV[mesSelecionado - 1]}/${ano}`}>
                          {detalheProjeto.alocacaoColaboradoresMes.length === 0 ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                              Nenhum colaborador alocado neste mês.
                            </div>
                          ) : (
                            <BarChart data={detalheProjeto.alocacaoColaboradoresMes} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 4 }} barCategoryGap="24%">
                              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                              <XAxis type="number" domain={[0, 100]} stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                              <YAxis dataKey="colaborador" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={150} />
                              <Tooltip
                                contentStyle={tooltipStyle}
                                formatter={(v) => [`${v}%`, "Alocação"]}
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.colaboradorCompleto || ""}
                              />
                              <Bar dataKey="pct" name="Alocação" radius={[0, 4, 4, 0]} maxBarSize={20}>
                                {detalheProjeto.alocacaoColaboradoresMes.map((c, i) => <Cell key={i} fill={corPct(C, c.pct)} />)}
                                <LabelList dataKey="pct" position="right" fontSize={10} fill={C.muted} formatter={(v) => `${v}%`} />
                              </Bar>
                            </BarChart>
                          )}
                        </ChartCard>

                        <ChartCard C={C} isLight={isLight} height={Math.max(220, detalheProjeto.rankingCustoColaboradoresMes.length * 30)}
                          titulo="Ranking de custo por colaborador"
                          subtitulo={`% alocado × custo individual, em ${projetoSelecionadoNome} — ${MESES_ABREV[mesSelecionado - 1]}/${ano}`}>
                          {detalheProjeto.rankingCustoColaboradoresMes.length === 0 ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                              Nenhum custo de pessoal neste mês.
                            </div>
                          ) : (
                            <BarChart data={detalheProjeto.rankingCustoColaboradoresMes} layout="vertical" margin={{ top: 4, right: 70, bottom: 4, left: 4 }} barCategoryGap="24%">
                              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                              <XAxis type="number" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                              <YAxis dataKey="colaborador" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={150} />
                              <Tooltip
                                contentStyle={tooltipStyle}
                                formatter={(v) => [formatCurrency(v), "Custo de pessoal"]}
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.colaboradorCompleto || ""}
                              />
                              <Bar dataKey="custo" name="Custo de pessoal" fill={C.brand} radius={[0, 4, 4, 0]} maxBarSize={20}>
                                <LabelList dataKey="custo" position="right" fontSize={10} fill={C.muted} formatter={fmtCompact} />
                              </Bar>
                            </BarChart>
                          )}
                        </ChartCard>
                      </div> */}
                    </>
                  )}
                </>
              )}
              <ChartCard
                C={C} isLight={isLight} height={300}
                titulo="Saúde da alocação ao longo do tempo"
                subtitulo="Média mensal do time — % alocado (capado a 100%), % ocioso e % sobrealocado, com meta de 100% de referência"
              >
                <LineChart data={saudeAlocacao} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="mes" stroke={C.muted} fontSize={12} tickLine={false} axisLine={false} dy={6} />
                  <YAxis stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={40} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [`${v}%`, name]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={100} stroke={C.muted} strokeDasharray="4 4" label={{ value: "Meta 100%", position: "insideTopRight", fontSize: 10, fill: C.muted }} />
                  <Line type="monotone" dataKey="alocado" name="% Alocado" stroke={C.brand} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="ocioso" name="% Ocioso" stroke={C.warning} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ChartCard>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <ChartCard
                  C={C} isLight={isLight}
                  height={Math.max(240, custoPorProjeto.length * 34)}
                  titulo="Ranking de projetos por custo de pessoal"
                  subtitulo={`Acumulado em ${ano} — % de alocação × custo total mensal de cada colaborador`}
                >
                  {custoPorProjeto.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                      Nenhum custo de pessoal alocado a projetos em {ano}.
                    </div>
                  ) : (
                    <BarChart data={custoPorProjeto} layout="vertical" margin={{ top: 4, right: 70, bottom: 4, left: 4 }} barCategoryGap="24%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis type="number" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                      <YAxis dataKey="projeto" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={140} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v) => [formatCurrency(v), "Custo de pessoal"]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.projetoCompleto || ""}
                      />
                      <Bar dataKey="total" name="Custo de pessoal" fill={C.brand} radius={[0, 4, 4, 0]} maxBarSize={22}>
                        <LabelList dataKey="total" position="right" fontSize={10} fill={C.muted} formatter={fmtCompact} />
                      </Bar>
                    </BarChart>
                  )}
                </ChartCard>

                <ChartCard
                  C={C} isLight={isLight}
                  height={Math.max(240, ociosidadeAcumulada.length * 34)}
                  titulo="Ranking de ociosidade acumulada"
                  subtitulo={`Soma de (100% − alocação) mês a mês em ${ano} — top ${MAX_COLABORADORES_OCIOSIDADE}, em meses ociosos equivalentes`}
                >
                  {ociosidadeAcumulada.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                      Nenhuma ociosidade acumulada — time 100% alocado em todos os meses.
                    </div>
                  ) : (
                    <BarChart data={ociosidadeAcumulada} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 4 }} barCategoryGap="24%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis type="number" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}m`} />
                      <YAxis dataKey="colaborador" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={160} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v, _n, item) => [`${v} meses (${item.payload.pctAcumulado}% acumulado)`, "Ociosidade"]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.colaboradorCompleto || ""}
                      />
                      <Bar dataKey="mesesOciosos" name="Ociosidade" fill={C.warning} radius={[0, 4, 4, 0]} maxBarSize={22}>
                        <LabelList dataKey="mesesOciosos" position="right" fontSize={10} fill={C.muted} formatter={(v) => `${v}m`} />
                      </Bar>
                    </BarChart>
                  )}
                </ChartCard>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
